"use client";

import React from "react";
import { motion, useReducedMotion } from "framer-motion";
import { MapPin, Phone, Mail, ArrowUpRight } from "lucide-react";
import { Link } from "@/components/common/Link";
import SectionContainer from "@/components/common/SectionContainer";
import { Separator } from "@/components/ui/separator";
import ConnectForm from "@/components/forms/ConnectForm";
export default function ContactHero() {
  const prefersReducedMotion = useReducedMotion();
  const transitionConfig = {
    duration: prefersReducedMotion ? 0.01 : 0.8,
    ease: [0.22, 1, 0.36, 1] as const
  };
  const containerVariants = {
    hidden: {
      opacity: 0
    },
    show: {
      opacity: 1,
      transition: {
        staggerChildren: prefersReducedMotion ? 0 : 0.1
      }
    }
  };
  const itemVariants = {
    hidden: {
      opacity: 0,
      y: prefersReducedMotion ? 0 : 20
    },
    show: {
      opacity: 1,
      y: 0,
      transition: transitionConfig
    }
  };
  const contactItems = [{
    label: "Location",
    value: "3029 Midway Rd, Southlake, TX 76092",
    type: "map",
    to: "https://www.google.com/maps/search/3029+Midway+Rd,+Southlake,+TX+76092",
    icon: MapPin
  }, {
    label: "Direct Line",
    value: "(817) 329-1668",
    type: "phone",
    callHref: "tel:8173291668",
    textHref: "sms:8173291668",
    icon: Phone
  }, {
    label: "Inquiries",
    value: "info@paradisecovetx.com",
    type: "email",
    mailHref: "mailto:info@paradisecovetx.com",
    icon: Mail
  }];
  return <section id="contacthero" data-nav="light" className="relative bg-background text-foreground py-16 md:py-24 border-b border-border">
      <SectionContainer>
        <motion.div variants={containerVariants} initial="hidden" whileInView="show" viewport={{
        once: true,
        amount: 0.2
      }} className="grid grid-cols-1 lg:grid-cols-12 gap-12 lg:gap-16 items-start">
          {/* Left Column: Heading & Form */}
          <div className="lg:col-span-7 flex flex-col space-y-8">
            <motion.div variants={itemVariants} className="space-y-4">
              {/* Eyebrow */}
              <div className="flex items-center gap-3">
                <span className="text-xs font-sans tracking-widest uppercase font-medium text-foreground">
                  {"CONNECT"}
                </span>
                <span className="h-px w-12 bg-border block" aria-hidden="true" />
              </div>

              {/* Main Headline */}
              <h1 className="text-4xl sm:text-5xl lg:text-6xl font-serif font-normal tracking-tight text-foreground leading-[1.1]">
                Let's plan your event
              </h1>

              {/* Intro Body */}
              <p className="text-lg md:text-xl font-serif text-muted-foreground leading-relaxed max-w-2xl">
                Schedule a tour, ask about availability, or start planning your celebration at Paradise Cove.
              </p>
            </motion.div>

            {/* Inquiry Form */}
            <motion.div variants={itemVariants}>
              <ConnectForm />
            </motion.div>
          </div>

          {/* Right Column: Contact Directory */}
          <div className="lg:col-span-5 flex flex-col justify-between h-full pt-2 lg:pt-14 space-y-8">
            <motion.div variants={itemVariants} className="border border-border rounded-lg bg-card p-6 md:p-8 space-y-8 shadow-sm">
              <div className="space-y-1">
                <h2 className="text-xs font-sans font-medium uppercase tracking-widest text-muted-foreground">
                  Directory
                </h2>
                <p className="text-sm font-serif text-foreground">
                  Direct communication channels for reservations and tours.
                </p>
              </div>

              <Separator className="bg-border" />

              <div className="space-y-8">
                {contactItems.map((item, i) => <div key={item.label} data-index={i} className="space-y-2">
                    <div className="flex items-center gap-2 text-xs font-sans uppercase font-medium tracking-widest text-muted-foreground">
                      <item.icon className="w-3.5 h-3.5 text-foreground" />
                      <span>{item.label}</span>
                    </div>

                    {item.type === "map" && item.to && <div className="group">
                        <Link to={item.to} newTab className="inline-flex items-start justify-between w-full text-foreground hover:text-foreground/80 transition-colors">
                          <span className="text-xl md:text-2xl font-serif font-medium leading-snug">
                            {item.value}
                          </span>
                          <ArrowUpRight className="w-5 h-5 ml-2 mt-1 shrink-0 text-muted-foreground group-hover:text-foreground transition-colors" />
                        </Link>
                      </div>}

                    {item.type === "phone" && <div className="space-y-2">
                        <div className="text-xl md:text-2xl font-serif font-medium text-foreground">
                          {item.value}
                        </div>
                        <div className="flex items-center gap-3 text-xs font-sans uppercase tracking-widest pt-1">
                          <Link to={item.callHref} className="text-foreground underline underline-offset-4 hover:text-foreground/70 transition-colors font-medium">
                            Call
                          </Link>
                          <span className="text-muted-foreground" aria-hidden="true">
                            •
                          </span>
                          <Link to={item.textHref} className="text-foreground underline underline-offset-4 hover:text-foreground/70 transition-colors font-medium">
                            Text
                          </Link>
                        </div>
                      </div>}

                    {item.type === "email" && <div>
                        <Link to={item.mailHref} className="inline-flex items-start justify-between w-full text-foreground hover:text-foreground/80 transition-colors group">
                          <span className="text-xl md:text-2xl font-serif font-medium break-all">
                            {item.value}
                          </span>
                          <ArrowUpRight className="w-5 h-5 ml-2 mt-1 shrink-0 text-muted-foreground group-hover:text-foreground transition-colors" />
                        </Link>
                      </div>}

                    {i < contactItems.length - 1 && <Separator className="bg-border/60 mt-6" />}
                  </div>)}
              </div>
            </motion.div>

            {/* Subtle Operational Note */}
            <motion.div variants={itemVariants} className="px-4 py-3 rounded-lg border border-border/80 bg-background text-xs font-sans text-muted-foreground flex items-center justify-between">
              <span className="tracking-wide">Response time: within 24 business hours</span>
              <span className="inline-block w-2 h-2 rounded-full bg-foreground" />
            </motion.div>
          </div>
        </motion.div>
      </SectionContainer>
    </section>;
}